import { z } from 'zod';
import { dateSchema, repeatSchema, sourceSchema, sprintKeySchema, sprintTypeSchema, taskStatusSchema, timeSchema, uuidSchema, } from './schema.js';
/**
 * Прикладной REST для агента (раздел 8 архитектуры, этап 14 плана).
 *
 * Схемы лежат в общем пакете, а не на сервере: ими же проверяет входы и
 * читает ответы MCP-сервер (этап 15), и это единственный способ, при котором
 * агент и сервер не разойдутся в понимании модели — второй экземпляр той же
 * формы разъедется с первым и узнают об этом из бага.
 *
 * Здесь нет ничего про шифрование: API отдаёт содержимое открытым текстом,
 * потому что в состоянии A ключ у сервера (7.5 архитектуры). Фильтры при этом
 * только по открытым полям — спринт, статус, срок, проект, тег через слепой
 * индекс. Параметра поиска по тексту нет намеренно, и добавлять его нельзя:
 * он единственный, что помешает перейти к сквозному шифрованию.
 */
// --------------------------------------------------------------------------
// Токены и скоупы
// --------------------------------------------------------------------------
/**
 * Скоупы токена. `destroy` нужен для того, что не вернуть одним нажатием:
 * удаление и стирание, импорт, массовые операции свыше порога. Обычная
 * работа агента — записать, закрыть, перенести, разобрать Входящие пачкой до
 * порога — укладывается в `write` (решение владельца, 2026-09-20).
 */
export const API_SCOPES = ['read', 'write', 'destroy'];
export const apiScopeSchema = z.enum(API_SCOPES);
/** Сколько задач за один массовый вызов. */
export const BULK_LIMIT = 200;
/**
 * Порог, выше которого массовая операция считается разрушительной: нужен
 * скоуп `destroy`, а в MCP — явное `confirm` (3.14.5, раздел 9 архитектуры).
 * Один порог на оба места, чтобы агент не упирался в отказ сервера после
 * того, как уже получил согласие человека.
 */
export const BULK_CONFIRM_THRESHOLD = 20;
/** Как токен показывается снаружи: префикс — для сканеров секретов. */
export const API_TOKEN_PREFIX = 'ndl_';
export const createTokenRequestSchema = z.object({
    name: z.string().trim().min(1).max(64),
    scopes: z.array(apiScopeSchema).min(1),
});
export const apiTokenSummarySchema = z.object({
    id: uuidSchema,
    name: z.string(),
    scopes: z.array(apiScopeSchema),
    /** Хвост токена — чтобы отличить один от другого, не раскрывая его. */
    hint: z.string(),
    created_at: z.string(),
    last_used_at: z.string().nullable(),
    revoked_at: z.string().nullable(),
});
export const createTokenResponseSchema = apiTokenSummarySchema.extend({
    /** Показывается один раз: в базе остаётся только хеш. */
    token: z.string(),
});
/** След вызова: когда, что и с какими задачами (этап 14 плана). */
export const apiCallSchema = z.object({
    at: z.string(),
    method: z.string(),
    path: z.string(),
    status: z.number().int(),
    refs: z.array(z.string()),
});
// --------------------------------------------------------------------------
// Ссылки и выборки
// --------------------------------------------------------------------------
/** Ссылка на задачу: uuid, `Н-42`, `H-42` или голое `42` (`parseRef`). */
export const taskRefSchema = z.string().trim().min(1).max(64);
/**
 * Спринт в запросе: ключ либо слово относительно недели человека. «Текущая»
 * считается по его часовому поясу и длине спринта, как автоперенос.
 */
export const RELATIVE_SPRINTS = ['current', 'next', 'prev'];
export const sprintSelectorSchema = z.union([sprintKeySchema, z.enum(RELATIVE_SPRINTS)]);
const limitSchema = z.coerce.number().int().positive().max(500).default(200);
export const listTasksQuerySchema = z.object({
    /** Ключ, относительное слово или `inbox` — задачи без спринта. */
    sprint: z.union([sprintSelectorSchema, z.literal('inbox')]).optional(),
    status: taskStatusSchema.optional(),
    /** Тег по слепому индексу: сервер сравнивает HMAC, не видя названия. */
    tag: z.string().trim().min(1).optional(),
    /** Проект — ссылка на задачу-зонтик. */
    project: taskRefSchema.optional(),
    due_from: dateSchema.optional(),
    due_until: dateSchema.optional(),
    updated_since: z.iso.datetime({ offset: true }).optional(),
    limit: limitSchema,
});
// --------------------------------------------------------------------------
// Задача снаружи
// --------------------------------------------------------------------------
export const apiChecklistItemSchema = z.object({
    id: uuidSchema,
    text: z.string(),
    done: z.boolean(),
    order_key: z.string(),
});
/** Связь глазами задачи: кого держит и кто держит её. */
export const apiLinkSchema = z.object({
    id: uuidSchema,
    /** Ссылка на задачу по ту сторону связи. */
    ref: z.string(),
    title: z.string(),
    status: taskStatusSchema,
});
export const apiTaskSchema = z.object({
    id: uuidSchema,
    number: z.number().int().positive(),
    /** Тот же номер словами: `Н-42`. Им агент ссылается на задачу в ответах. */
    ref: z.string(),
    title: z.string(),
    notes: z.string().nullable(),
    status: taskStatusSchema,
    sprint: sprintKeySchema.nullable(),
    due: dateSchema.nullable(),
    due_time: timeSchema.nullable(),
    /** Повтор (3.19): есть только у последнего экземпляра серии. */
    repeat: repeatSchema.nullable(),
    parent_id: uuidSchema.nullable(),
    /** Ссылка на проект, если задача вложена. */
    parent_ref: z.string().nullable(),
    order_key: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    done_at: z.string().nullable(),
    deleted_at: z.string().nullable(),
    created_by: sourceSchema,
    tags: z.array(z.string()),
});
/** Полная карточка: то же плюс чек-лист, связи и вложенные. */
export const apiTaskDetailSchema = apiTaskSchema.extend({
    checklist: z.array(apiChecklistItemSchema),
    /** Кого держит эта задача. */
    blocks: z.array(apiLinkSchema),
    /** Кто держит её. */
    blocked_by: z.array(apiLinkSchema),
    /** Вложенные задачи, если это проект. */
    children: z.array(z.object({ ref: z.string(), title: z.string(), status: taskStatusSchema })),
});
// --------------------------------------------------------------------------
// Правки
// --------------------------------------------------------------------------
const titleSchema = z.string().trim().min(1).max(500);
const notesSchema = z.string().max(20_000).nullable();
const tagSchema = z.string().trim().min(1).max(64);
/**
 * Создание: обязателен только заголовок (3.14.2). `#тег` внутри заголовка
 * разбирается тем же кодом, что и строка быстрой записи. Без спринта задача
 * уходит во Входящие — «запиши: продлить страховку» не спрашивает, на какую
 * неделю.
 */
export const createTaskRequestSchema = z.object({
    title: titleSchema,
    notes: notesSchema.optional(),
    status: taskStatusSchema.optional(),
    sprint: sprintSelectorSchema.nullable().optional(),
    due: dateSchema.nullable().optional(),
    due_time: timeSchema.nullable().optional(),
    repeat: repeatSchema.nullable().optional(),
    parent: taskRefSchema.nullable().optional(),
    tags: z.array(tagSchema).max(50).optional(),
});
/**
 * Правка — только присланные поля; `updated_at` сервер проставляет сам, а
 * `done_at` едет вместе со статусом, как в интерфейсе.
 */
export const patchTaskRequestSchema = z
    .object({
    title: titleSchema.optional(),
    notes: notesSchema.optional(),
    status: taskStatusSchema.optional(),
    sprint: sprintSelectorSchema.nullable().optional(),
    due: dateSchema.nullable().optional(),
    due_time: timeSchema.nullable().optional(),
    repeat: repeatSchema.nullable().optional(),
    parent: taskRefSchema.nullable().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, { message: 'в правке нет ни одного поля' });
/** Массовая правка: то же без текста — один заголовок на двести задач не нужен никому. */
export const bulkPatchSchema = z
    .object({
    status: taskStatusSchema.optional(),
    sprint: sprintSelectorSchema.nullable().optional(),
    due: dateSchema.nullable().optional(),
    due_time: timeSchema.nullable().optional(),
    parent: taskRefSchema.nullable().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, { message: 'в правке нет ни одного поля' });
export const bulkRequestSchema = z.object({
    refs: z.array(taskRefSchema).min(1).max(BULK_LIMIT),
    patch: bulkPatchSchema,
});
export const bulkResponseSchema = z.object({
    updated: z.array(z.string()),
    /** Задачи, у которых правка ничего не меняла: уже в этом статусе, уже в этом спринте. */
    unchanged: z.array(z.string()),
});
export const addChecklistItemRequestSchema = z.object({ text: z.string().trim().min(1).max(2000) });
export const patchChecklistItemRequestSchema = z
    .object({
    text: z.string().trim().min(1).max(2000).optional(),
    done: z.boolean().optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, { message: 'в правке нет ни одного поля' });
/** Связь заводится с любой стороны, но ровно с одной за раз. */
export const addLinkRequestSchema = z
    .object({
    blocks: taskRefSchema.optional(),
    blocked_by: taskRefSchema.optional(),
})
    .refine((body) => (body.blocks === undefined) !== (body.blocked_by === undefined), {
    message: 'нужно ровно одно из полей: blocks или blocked_by',
});
export const addTagRequestSchema = z.object({ tag: tagSchema });
export const rolloverRequestSchema = z.object({
    /** Куда переносить; по умолчанию — следующий спринт после указанного. */
    to: sprintSelectorSchema.optional(),
});
export const rolloverResponseSchema = z.object({
    target: sprintKeySchema,
    moved: z.array(z.string()),
});
// --------------------------------------------------------------------------
// Настройки, выгрузка и импорт
// --------------------------------------------------------------------------
export const apiSettingsSchema = z.object({
    sprint_type: sprintTypeSchema,
    /** Пояс, по которому считаются «текущая неделя» и автоперенос; присылает приложение. */
    timezone: z.string().nullable(),
});
export const patchSettingsRequestSchema = z.object({ sprint_type: sprintTypeSchema });
export const EXPORT_FORMAT = 'nedelnik-export';
export const EXPORT_VERSION = 1;
const exportTaskSchema = z.object({
    id: uuidSchema,
    number: z.number().int().positive(),
    title: z.string(),
    notes: z.string().nullable(),
    status: taskStatusSchema,
    sprint: sprintKeySchema.nullable(),
    due: dateSchema.nullable(),
    due_time: timeSchema.nullable(),
    // Необязательны: файлы, выгруженные до повторов (3.19), должны загружаться.
    repeat: repeatSchema.nullable().optional(),
    series_id: uuidSchema.nullable().optional(),
    parent_id: uuidSchema.nullable(),
    order_key: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    done_at: z.string().nullable(),
    deleted_at: z.string().nullable(),
    created_by: sourceSchema,
});
const exportChecklistItemSchema = z.object({
    id: uuidSchema,
    task_id: uuidSchema,
    text: z.string(),
    done: z.boolean(),
    order_key: z.string(),
    created_at: z.string(),
    deleted_at: z.string().nullable(),
});
const exportLinkSchema = z.object({
    id: uuidSchema,
    blocker_id: uuidSchema,
    blocked_id: uuidSchema,
    created_at: z.string(),
    deleted_at: z.string().nullable(),
});
const exportTagSchema = z.object({
    id: uuidSchema,
    task_id: uuidSchema,
    tag: z.string(),
    created_at: z.string(),
    deleted_at: z.string().nullable(),
});
/**
 * Файл выгрузки (3.13.5, 3.18.7): всё содержимое открытым текстом, включая
 * корзину — надгробие в файле честнее, чем пропавшая задача.
 */
export const exportFileSchema = z.object({
    format: z.literal(EXPORT_FORMAT),
    version: z.literal(EXPORT_VERSION),
    exported_at: z.string(),
    sprint_type: sprintTypeSchema,
    tasks: z.array(exportTaskSchema),
    checklist_items: z.array(exportChecklistItemSchema),
    task_links: z.array(exportLinkSchema),
    task_tags: z.array(exportTagSchema),
});
/**
 * Импорт принимает тот же файл, но заводит **новые** записи: свои id и
 * номера, связи внутри файла сохраняются, ничего не сливается с тем, что уже
 * есть (решение владельца, 2026-09-20). Корзина из файла не восстанавливается.
 */
export const importRequestSchema = exportFileSchema;
export const importResponseSchema = z.object({
    tasks: z.number().int().nonnegative(),
    checklist_items: z.number().int().nonnegative(),
    task_links: z.number().int().nonnegative(),
    task_tags: z.number().int().nonnegative(),
});
//# sourceMappingURL=api.js.map